import {
  FC,
  FormEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";
import { usePostHog } from "posthog-js/react";
import { useCookies } from "react-cookie";
import Input from "apps/website/components/form/Input/Input";
import Form from "apps/website/components/form/Form/Form";
import Grid from "apps/website/components/layout/Grid/Grid";
import Column from "apps/website/components/layout/Column/Column";
import Button from "apps/website/components/base/Button/Button";
import IconButton from "apps/website/components/base/IconButton/IconButton";
import Text from "apps/website/components/base/Text/Text";
import { useCustomerStore, useDiscountStore } from "@./state";
import {
  getFieldValue,
  getFlowFieldValuesForAction,
  getFlowProduct,
} from "libs/state/src/lib/stores/useFormServiceStore";
import { useAPI } from "apps/website/hooks/useAPI";
import { IPlanSummaryResponse } from "@forms/schema";
import {
  loadMentionMeRefereefind,
} from "apps/website/utils/mentionme/loadMentionMe";
import {
  legacySizeCollectionMap,
} from "apps/website/components/base/Text/Text.map";
import { useDatadog } from "@auth/client-sdk-react";

import { LoadingSpinner } from "@/admin-components";
import { DisplayState } from "@/constants/state";

import {
  ACTION_TRACKING_COOKIE_NAMES,
  getActionTrackingInfo,
} from "../../../../utils/tracking/getActionTrackingInfo";

interface ISummaryDiscountCode {
  defaultDiscountCode?: string;
  flowId: string;
  action: string;
  allowReferralCode?: boolean;
  queryParam?: string;
}

const isRejectedResponse = (response: { success: boolean; responseBody?: unknown }): boolean => (
  !response.success || !!(response.responseBody as IPlanSummaryResponse | undefined)?.rejectedDiscountCode
);

/**
 * This component contains a lot of logging to help us debug the discount code flow,
 * as we are experience issues (shortcut ticket 13487).
 * Once this issue is resolved, we can remove some and just leave the essential ones.
 */
export const SummaryDiscountCode: FC<ISummaryDiscountCode> = ({
  defaultDiscountCode,
  flowId,
  action,
  allowReferralCode,

}) => {

  const formsActionsAPI = useAPI().FormActions;

  const posthog = usePostHog();
  const datadog = useDatadog();

  const [ cookies ] = useCookies(ACTION_TRACKING_COOKIE_NAMES);

  const {
    discountCodes: discountCodesInStore,
    setDiscountCode: setDiscountCodeInStore,
    clearDiscountCode: clearDiscountCodeInStore,
    _hasHydrated: hasHydrated,
  } = useDiscountStore();
  const [ displayDiscountCode, setDisplayDiscountCode ] = useState<string | undefined>(undefined);
  const [ discountCodeState, setDiscountCodeState ] = useState<DisplayState>(
    DisplayState.PROCESSING,
  );
  const [ localDiscountCode, setLocalDiscountCode ] = useState<string | undefined>(undefined);
  const { customerIds } = useCustomerStore();
  const [ showDiscount, setShowDiscount ] = useState<boolean>(true);

  useEffect(() => {
    if (window.MentionMeFiredTags) {
      const situation = "checkout";
      const implementation = "link";
      if (window.MentionMeFiredTags[implementation + situation]) {
        delete window.MentionMeFiredTags[implementation + situation];
      }
    }
  }, [ ]);

  useEffect(() => {
    // Don't run until the store has hydrated from localStorage
    if (!hasHydrated) {
      datadog.logger.info(`[SummaryDiscountCode] Waiting for store hydration - flowId: ${flowId}`, {
        flowId,
        hasHydrated,
      });
      return;
    }

    async function setupDiscounts() {
      const existingCode = discountCodesInStore.find((dc) => dc.flowId === flowId)?.discountCode;
      datadog.logger.info(`[SummaryDiscountCode] setupDiscounts - START - flowId: ${flowId}, defaultDiscountCode: ${defaultDiscountCode}, existingDiscountInStore: ${existingCode}, hasHydrated: ${hasHydrated}`, {
        flowId,
        defaultDiscountCode,
        existingDiscountCode: existingCode,
        hasHydrated,
      });

      if (getFieldValue(flowId, "flowTrialType", "0") && !!getFieldValue(flowId, "flowTrialType", "0")?.data.submitValue) {
        datadog.logger.info(`[SummaryDiscountCode] Trial type detected, clearing discount code - currentCode: ${existingCode}`, {
          flowId,
          existingDiscountCode: existingCode,
        });
        setDiscountCodeState(DisplayState.READY);
        if (existingCode) {
          datadog.logger.info(`[SummaryDiscountCode] Clearing discount code from store for trial type - removingCode: ${existingCode}`, {
            flowId,
            existingDiscountCode: existingCode,
          });
          clearDiscountCodeInStore(flowId);
        }
        setDisplayDiscountCode(undefined);
        setLocalDiscountCode(undefined);
        setShowDiscount(false);
        return;
      }
      setShowDiscount(true);

      if (!existingCode && defaultDiscountCode) {
        datadog.logger.info(`[SummaryDiscountCode] No discount code found for flow, setting default - flowId: ${flowId}, defaultDiscountCode: ${defaultDiscountCode}`, {
          flowId,
          defaultDiscountCode,
        });
        setDiscountCodeInStore(flowId, defaultDiscountCode);
      } else {
        datadog.logger.info(`[SummaryDiscountCode] Already have a discount with flow id - flowId: ${flowId}, existingCode: ${existingCode}`, {
          flowId,
          existingDiscountCode: existingCode,
        });
      }
      setDiscountCodeState(DisplayState.READY);
      datadog.logger.info(`[SummaryDiscountCode] setupDiscounts - END - flowId: ${flowId}, currentDiscountInStore: ${existingCode}`, {
        flowId,
        existingDiscountCode: existingCode,
      });
    }
    void setupDiscounts();
  }, [ hasHydrated ]);

  useEffect(() => {
    if (discountCodesInStore.find((dc) => dc.flowId === flowId)?.discountCode !== localDiscountCode) {
      setLocalDiscountCode(discountCodesInStore.find((dc) => dc.flowId === flowId)?.discountCode);
    }
  }, [ discountCodesInStore, flowId ]);

  const submitMMCode = useCallback(
    () => {
      setDiscountCodeState(DisplayState.PROCESSING);
      const storeCode = discountCodesInStore.find((dc) => dc.flowId === flowId)?.discountCode;

      async function inner() {
        const featureFlags = posthog.featureFlags.getFlagVariants();
        const fieldSubmitValuesMap = getFlowFieldValuesForAction(flowId);
        if (localDiscountCode) {

          fieldSubmitValuesMap.set("discountCode", { value: localDiscountCode });

        }
        await formsActionsAPI
          .performAction<never>(
            action,
            fieldSubmitValuesMap,
            getFlowProduct(flowId),
            flowId,
            undefined,
            featureFlags,
            getActionTrackingInfo(cookies),
            datadog.logger,
          )
          .then((response) => {
            if (isRejectedResponse(response)) {

              setDiscountCodeState(DisplayState.ERROR);
            } else {

              setDiscountCodeInStore(flowId, localDiscountCode);
              const summary = response.responseBody as IPlanSummaryResponse | undefined;
              setDisplayDiscountCode(summary?.discount?.body?.value);
              setDiscountCodeState(DisplayState.READY);
            }
          })
          .catch((error) => {
            datadog.logger.error(`[SummaryDiscountCode] Error validating MM discount code - attemptedCode: ${localDiscountCode}, currentStoreCode: ${storeCode}`, {
              error,
              flowId,
              localDiscountCode,
              storeCode,
            });
            setDiscountCodeState(DisplayState.ERROR);
          });
      }
      void inner();
    },
    [ formsActionsAPI, action, flowId, localDiscountCode, setDiscountCodeInStore ],
  );

  const onSubmitDiscountCode: FormEventHandler = useCallback(
    (event): void => {
      event.preventDefault();
      setDiscountCodeState(DisplayState.PROCESSING);
      const storeCode = discountCodesInStore.find((dc) => dc.flowId === flowId)?.discountCode;
      datadog.logger.info(`[SummaryDiscountCode] onSubmitDiscountCode - START - flowId: ${flowId}, localDiscountCode: ${localDiscountCode}, currentStoreCode: ${storeCode}`, {
        flowId,
        localDiscountCode,
        storeCode,
      });

      const fieldSubmitValuesMap = getFlowFieldValuesForAction(flowId);
      if (localDiscountCode) {
        fieldSubmitValuesMap.set("discountCode", { value: localDiscountCode });
        datadog.logger.info(`[SummaryDiscountCode] Added discount code to fieldSubmitValuesMap for form submit - localDiscountCode: ${localDiscountCode}, currentStoreCode: ${storeCode}`, {
          flowId,
          localDiscountCode,
          storeCode,
        });
      }

      void formsActionsAPI
        .performAction<never>(
          action,
          fieldSubmitValuesMap,
          getFlowProduct(flowId),
          flowId,
          undefined,
          undefined,
          getActionTrackingInfo(cookies),
          datadog.logger,
        )
        .then((response) => {
          if (isRejectedResponse(response)) {
            datadog.logger.info(`[SummaryDiscountCode] Form submit discount code validation failed - attemptedCode: ${localDiscountCode}, currentStoreCode: ${storeCode}`, {
              flowId,
              localDiscountCode,
              storeCode,
            });
            setDiscountCodeState(DisplayState.ERROR);
          } else {
            datadog.logger.info(`[SummaryDiscountCode] Form submit discount code validation succeeded - localDiscountCode: ${localDiscountCode}`, {
              flowId,
              localDiscountCode,
            });
            setDiscountCodeInStore(flowId, localDiscountCode);
            setDisplayDiscountCode((response.responseBody as IPlanSummaryResponse | undefined)?.discount?.body?.value);
            setDiscountCodeState(DisplayState.READY);
          }
        })
        .catch((error) => {
          datadog.logger.error(`[SummaryDiscountCode] Error validating form submit discount code - attemptedCode: ${localDiscountCode}, currentStoreCode: ${storeCode}`, {
            error,
            flowId,
            localDiscountCode,
            storeCode,
          });
          setDiscountCodeState(DisplayState.ERROR);
        });
    },
    [ formsActionsAPI, action, flowId, localDiscountCode, setDiscountCodeInStore ],
  );

  useEffect(() => {
    const firstName = getFieldValue(flowId, "firstName", "0")?.data?.submitValue?.toString() || "";
    const surname = getFieldValue(flowId, "lastName", "0")?.data?.submitValue?.toString() || "";
    const email = getFieldValue(flowId, "emailAddress", "0")?.data?.submitValue?.toString() || "";
    const result = loadMentionMeRefereefind({
      fullname: `${firstName} ${surname}`,
      firstname: firstName,
      surname,
      email,
      customer_id: customerIds.find((dc) => dc.flowId === flowId)?.customerId || "",
    });
    result();
  }, [ customerIds, flowId ]);

  return (
    <div>
      { showDiscount &&
      <>
        { discountCodeState === DisplayState.READY && (
          <>
            <Grid>
              { discountCodesInStore.find((dc) => dc.flowId === flowId
                && dc.discountCode && dc.discountCode !== defaultDiscountCode) ? (
                  <Column justify="between" align="center" direction="row">
                    <IconButton
                      label="Discount code"
                      display="title"
                      icon="cross"
                      size="small"
                      onClick={() => setDiscountCodeState(DisplayState.EDITING)}
                      className="width-fit"
                    >
                      { discountCodesInStore.find((dc) => dc.flowId === flowId)?.discountCode }
                    </IconButton>
                    <Text tag="span" display="title">
                      { displayDiscountCode }
                    </Text>
                  </Column>
                ) : (
                  <Column direction="row" className="pt-2">

                    <Text size={legacySizeCollectionMap.bodySm} tag="div">
                      <button
                        onClick={() => setDiscountCodeState(DisplayState.EDITING)}
                        className="cursor-pointer"
                      >
                        Got a <u>discount code</u>
                      </button>
                    </Text>
                    { allowReferralCode && (
                      <Text tag="div" size={legacySizeCollectionMap.bodySm} className="inline" onClick={() => setDiscountCodeState(DisplayState.EDITING)}>

                    &nbsp;or&nbsp;
                        <div id="mmWrapper" className="underline inline"></div>?
                      </Text>
                    ) }
                  </Column>
                ) }
              <Column direction="row" className="hidden">
                <input
                  type="submit"
                  id="coupon-button"
                  onClick={() => submitMMCode()}
                  value="Apply"
                />
                <input id="coupon-value" type="text" onChange={(event) => setLocalDiscountCode(event.target.value)} />
              </Column>
            </Grid>
          </>
        ) }
        { discountCodeState === DisplayState.EDITING && (
          <Form action="" onSubmit={onSubmitDiscountCode}>
            <Grid>
              <Column>
                <button
                  onClick={() => setDiscountCodeState(DisplayState.READY)}
                  className="text-left"
                >
                  <u>Close</u>
                </button>
              </Column>
              <Column direction={{ lg: "row" }}>
                <Input
                  label="Discount code"
                  hideLabel
                  placeholder="Enter your code"
                  type="text"
                  value={localDiscountCode}
                  name="discount_code"
                  onChange={(event) => {
                    const newCode = event.target.value.toUpperCase();
                    datadog.logger.info(`[SummaryDiscountCode] Local discount code changed - oldCode: ${localDiscountCode}, newCode: ${newCode}`, {
                      flowId,
                      oldCode: localDiscountCode,
                      newCode,
                    });
                    setLocalDiscountCode(newCode);
                  }}
                  className="flex-1 mr-4"
                />
                <Button
                  type="submit"
                  disabled={
                    !localDiscountCode?.trim().length ||
                  localDiscountCode === discountCodesInStore.find((dc) => dc.flowId === flowId)?.discountCode
                  }
                  size="fit-content"
                >
                  Apply
                </Button>
              </Column>
              <>
                { discountCodesInStore.find((dc) => dc.flowId === flowId) && (
                  <Column className="border-solid border-2 border-orange box-border p-4">
                    <Text tag="span" align="center">
                      This discount code will replace any existing ones.
                    </Text>
                  </Column>
                ) }
              </>
            </Grid>
          </Form>
        ) }
        { discountCodeState === DisplayState.PROCESSING && (
          <Grid>
            <Column>
              <div className="w-fit">
                <LoadingSpinner color="black" size={32} />
              </div>
            </Column>
          </Grid>
        ) }
        { discountCodeState === DisplayState.ERROR && (
          <Grid>
            <Column>
              <div className="text-orange">
                <Text tag="span" color="inherit">
                  Sorry, this code is not valid.
                </Text>
              </div>
              <button
                onClick={() => setDiscountCodeState(DisplayState.EDITING)}
                className="text-left"
              >
                <u>Try again</u>
              </button>
            </Column>
          </Grid>
        ) }
      </>
      }
    </div>
  );
};
